/**
 * Pure helpers for the identifying metadata shown on the in-chat HITL gate card.
 *
 * The durable-card fallback can surface a task from a *different* (or terminated)
 * run because pending tasks carry no conversation uuid yet. Showing the feature
 * title + when the gate was raised lets a human recognize a foreign card at a
 * glance instead of acting on it blindly (see the 2026-08-17 review).
 *
 * Framework-free and unit-testable with plain Node.
 */

const MAX_TITLE_LENGTH = 80;

/**
 * Best-effort feature title for a gate card.
 *
 * Prefers a discrete field when the engine provides one (`feature_title` /
 * `title` — the companion backend change can populate either), and otherwise
 * falls back to the first non-empty line of `summary`, which today already
 * carries the title. Returns '' when nothing usable is present.
 *
 * @param {object|null|undefined} render  The gate's render blob.
 * @returns {string}
 */
export const deriveHitlFeatureTitle = (render) => {
  if (!render || typeof render !== 'object') return '';

  const explicit =
    render.feature_title ??
    render.featureTitle ??
    render.title ??
    (render.feature && typeof render.feature === 'object' ? render.feature.title : undefined);
  if (typeof explicit === 'string' && explicit.trim()) {
    return truncate(explicit.trim());
  }

  const summary = typeof render.summary === 'string' ? render.summary : '';
  const firstLine = summary
    .split('\n')
    .map((line) => line.trim())
    .find(Boolean);
  return firstLine ? truncate(firstLine) : '';
};

const truncate = (text) =>
  text.length > MAX_TITLE_LENGTH
    ? `${text.slice(0, MAX_TITLE_LENGTH - 1).trimEnd()}…`
    : text;

/**
 * Render-blob keys that are structural / already shown elsewhere, so they
 * must not be repeated as labelled "feedback" rows on the in-chat card.
 */
const HIDDEN_RENDER_DETAIL_KEYS = new Set([
  'ui_block',
  'block_type',
  'blockType',
  'conversation_uuid',
  'conversationUuid',
  'questions',
  'summary',
  'prompt',
  'brief',
  'agent_reply',
  'agentReply',
  'feature_title',
  'featureTitle',
  'title',
]);

/**
 * Extra human-facing fields from a HITL render blob (e.g. `north_star`,
 * `boulder`, `client_questions`) that the approval card must show as labelled
 * rows. Mirrors the My Tasks slide-out (`TaskDecisionSlideOut` detailRows).
 *
 * @param {object|null|undefined} render
 * @returns {Array<{ key: string, value: string }>}
 */
export const listHitlRenderDetails = (render) => {
  if (!render || typeof render !== 'object') return [];
  const rows = [];
  for (const [key, raw] of Object.entries(render)) {
    if (HIDDEN_RENDER_DETAIL_KEYS.has(key)) continue;
    if (raw == null || raw === false) continue;
    if (typeof raw === 'string') {
      const text = raw.trim();
      if (!text) continue;
      rows.push({ key, value: text });
      continue;
    }
    if (typeof raw === 'number' || typeof raw === 'boolean') {
      rows.push({ key, value: String(raw) });
      continue;
    }
    try {
      rows.push({ key, value: JSON.stringify(raw, null, 2) });
    } catch {
      // skip unserializable values
    }
  }
  return rows;
};

export default deriveHitlFeatureTitle;
